import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@mui/material';
import ColorIndicatorWithText from 'components/new/ColorIndicatorWithText';
import CurrencyRate from 'components/CurrencyRate';
import Row from 'components/Layout/Row';
import TransactionAllocator, {
  Transaction,
} from 'components/TransactionAllocator';
import { ActionTransactionSubTypes, ActionType } from 'further-types/lp/action';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import { CurrencyCode, FxDataFeed } from 'further-types/lp/vehicle';
import { Column } from 'further-ui/components/Layout';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { revaluation as rules } from 'further-ui/validations';
import api from 'lib/trpcClient';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { ProcessedRevaluation } from '../../helpers/processRevaluationHistory';
import TransactionAllocatorSkeleton from './TransactionAllocatorSkeleton';

interface HoldingClassTransactionAllocatorProps {
  revaluation: ProcessedRevaluation;
  holdingClassIndex: number;
  transactionTypeOptions: Array<{ label: string; value: string | null }>;
  vehicle: {
    _id: string;
    baseCurrency: CurrencyCode;
    fxDataFeed: FxDataFeed;
  };
  assetId: string;
  globalFxRate: CurrencyRateDetails | null;
  isFetchingRate: boolean;
  shouldHideValuationChangeAmount?: boolean;
}

const HoldingClassTransactionAllocator: React.FC<
  HoldingClassTransactionAllocatorProps
> = ({
  revaluation,
  holdingClassIndex,
  transactionTypeOptions,
  vehicle,
  assetId,
  globalFxRate,
  isFetchingRate,
  shouldHideValuationChangeAmount = false,
}) => {
  const { setValue, watch } = useFormContext<rules.RevaluationFormType>();

  const isNonLcy = revaluation.currency !== vehicle.baseCurrency;

  const holdingClassFxRate =
    watch(`revaluationCurrencyRates.${revaluation.holdingClassId}`) ??
    globalFxRate;

  const handleHoldingClassFxRateChange = (rate: CurrencyRateDetails) => {
    setValue(`revaluationCurrencyRates.${revaluation.holdingClassId}`, rate);
  };

  const {
    data: revaluationTransactionsData,
    isLoading: isTransactionsLoading,
  } = api.lpRevaluation.getRevaluationTransactions.useQuery(
    {
      assetId,
      vehicleId: vehicle._id,
      holdingClassId: revaluation.holdingClassId,
      revaluationDate: new CalendarDay(revaluation.date),
      newUnitPriceTcy: revaluation.newUnitPrice,
      currencyRate: holdingClassFxRate ?? undefined,
      transactionCurrency: revaluation.currency,
    },
    {
      enabled: !!assetId && !!vehicle._id,
    },
  );

  const baseTransactions = revaluationTransactionsData?.transactions ?? [];
  const totalStepUpTcy = revaluationTransactionsData?.totalStepUpTcy ?? 0;

  const existingTransactions =
    watch(`revaluationTransactions.${revaluation.holdingClassId}`) ?? [];

  const existingByKey = new Map(
    existingTransactions.map((t) => [
      `${t.investmentId}-${t.actionType}-${t.operationType}`,
      t,
    ]),
  );

  const transactions = baseTransactions.map((t) => {
    const key = `${t.investmentId}-${t.actionType}-${t.operationType}`;
    const existing = existingByKey.get(key);
    return existing?.actionType === t.actionType
      ? { ...t, transactionTypeId: existing.transactionTypeId }
      : t;
  });

  const onTransactionChange = (
    transactions: Array<
      Transaction & { investmentId: string; actionType: ActionType }
    >,
    holdingClassId: string,
  ) => {
    setValue(
      `revaluationTransactions.${holdingClassId}`,
      transactions.map((t) => ({
        ...t,
        id: t.id?.toString(),
        amountIncVatLcy: t.amountIncVatLcy ?? 0,
        vatRate: 0,
        transactionSubType: ActionTransactionSubTypes.CreditDebitPair,
      })),
    );
  };

  useEffect(() => {
    if (!baseTransactions.length) return;
    onTransactionChange(transactions, revaluation.holdingClassId);
  }, [baseTransactions]);

  if (isTransactionsLoading) {
    return <TransactionAllocatorSkeleton />;
  }

  return (
    <Accordion defaultExpanded disableGutters>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Row centered justify="space-between" fullWidth>
          <Typography variant="h5">
            <ColorIndicatorWithText
              listIndex={holdingClassIndex}
              text={revaluation.holdingClassName ?? ''}
            />
          </Typography>
          {!shouldHideValuationChangeAmount && (
            <Typography variant="h5" color="text.secondary">
              {numberToCurrencyString(totalStepUpTcy, {
                currency: revaluation.currency,
              })}
            </Typography>
          )}
        </Row>
      </AccordionSummary>
      <AccordionDetails>
        <Column spacing="xs">
          {isNonLcy && (
            <CurrencyRate
              currency={revaluation.currency}
              date={new CalendarDay(revaluation.date)}
              vehicle={vehicle}
              fxRate={holdingClassFxRate}
              onRateChange={handleHoldingClassFxRateChange}
              isFetchingRate={isFetchingRate}
            />
          )}
          <TransactionAllocator
            transactionTypeOptions={transactionTypeOptions ?? []}
            transactions={transactions}
            onChange={(transactions) =>
              onTransactionChange(transactions, revaluation.holdingClassId)
            }
            transactionCurrency={revaluation.currency}
            fxRate={holdingClassFxRate}
            vehicle={vehicle}
          />
        </Column>
      </AccordionDetails>
    </Accordion>
  );
};

export default HoldingClassTransactionAllocator;
